import { OktaAuth, TokenResponse } from "@okta/okta-auth-js";
import { appContext } from "../AppContext";

export default class Authentication
{
    private get localStorage() { return appContext().localStorageProvider; }
    private get sessionStorage() { return appContext().sessionStorageProvider; }
    private get cache() { return appContext().cache; }

    public getToken(): string
    {
        return appContext().localStorageProvider.getToken();
    }

    public setToken(value: string): void
    {
        appContext().localStorageProvider.setToken(value);
    }

    public async refreshToken(): Promise<string>
    {
        const securityProviderTypeId = this.localStorage.getUserDetails().providerType ?? '';
        switch (securityProviderTypeId)
        {
            case 'Native':
                throw new Error('Native authentication does not support refreshing tokens.');
            case 'AAD':
            case 'B2C':
                return this.refreshAadToken();
            case 'Okta':
                return this.refreshOktaToken();
            default:
                throw new Error('Not supported.');
        }
    }

    private async refreshAadToken(): Promise<string>
    {
        try
        {
            const tokenResponse = await appContext().msalProvider.acquireTokenSilent();
            if (!tokenResponse.accessToken)
            {
                throw new Error('Failed to refresh AAD token.');
            }

            this.localStorage.setToken(tokenResponse.accessToken);
            return tokenResponse.accessToken;
        }
        catch (tokenError)
        {
            throw new Error('Failed to refresh AAD token.');
        }
    }

    private async refreshOktaToken(): Promise<string>
    {
        let tokenStorage = JSON.parse(localStorage.getItem("okta-token-storage") ?? "{}");
        const config =
        {
            issuer: tokenStorage.idToken.issuer,
            clientId: tokenStorage.idToken.clientId,
            redirectUri: window.location.origin + "/implicit/callback",
            scopes: ['openid', 'email', 'profile'],
        };

        let authClient = new OktaAuth(config);

        try
        {
            const tokenResponse: TokenResponse = await authClient.token.getWithoutPrompt();
            if (!tokenResponse?.tokens?.idToken?.idToken)
            {
                throw new Error('Failed to refresh OKTA token.');
            }

            authClient.tokenManager.setTokens(tokenResponse.tokens);
            let idToken = tokenResponse.tokens.idToken.idToken;
            this.localStorage.setToken(idToken);
            return idToken;
        }
        catch (error)
        {
            throw new Error('Failed to refresh OKTA token.');
        };
    }

    public async logOut(reason?: LogOutReason): Promise<void>
    {
        await this.clearStorage();
        window.location.href = `/login${reason ? '?reason=' + LogOutReason[reason] : ''}`;
    }

    public async clearStorage(): Promise<void>
    {
        const currentLanguage = appContext().language.current;
        this.sessionStorage.clear();
        this.localStorage.clear();
        this.cache.clearAll();
        await appContext().language.setCurrent(currentLanguage);
    }
}

export enum LogOutReason
{
    NotSet = 0,
    SessionExpired,
    AccountIncomplete,
    LoginFailed,
    VersionChanged,
}
